import { WIRE_DATETIME_PATTERN } from "./wire-patterns";
import { z } from "zod";
import { getAddress } from "viem";

/** Partner holder badges (`holder.<slug>`) are passive: no lane, a fixed Aura value, not rarity (Aura v4). */
export const HOLDER_BADGE_PREFIX = "holder.";
export const HOLDER_BADGE_AURA = 100;
/** Most Aura a member gets from holder badges together: five badges count (Aura v5). */
export const HOLDER_AURA_CAP = 500;
export const isHolderBadgeId = (id: string): boolean => id.startsWith(HOLDER_BADGE_PREFIX);
export const holderBadgeId = (slug: string): string => `${HOLDER_BADGE_PREFIX}${slug}`;

/**
 * Collection activation (Aura v5). Each published holder badge is a collection; its active holders
 * are eligible members with the badge and at least one action badge (beyond signing up). Every
 * collection starts at level 1; a level needs `max(min, ceil(share × H))` active holders, `H` the
 * snapshot's distinct holder wallets. Active holders get the highest level's bonus (not cumulative).
 */
export const COMMUNITY_MILESTONES = [
  { level: 2, min: 10, share: 0.01, bonus: 100 },
  { level: 3, min: 25, share: 0.03, bonus: 150 },
  { level: 4, min: 50, share: 0.05, bonus: 250 },
] as const;
export type CommunityLevel = 1 | 2 | 3 | 4;
/** Aura for active holders of the top three collections by activation share (level 2 and up). */
export const COMMUNITY_PODIUM_BONUS = [150, 100, 50] as const;
/** Most community Aura a member gets across all collections. */
export const COMMUNITY_BONUS_CAP = 400;

/** Active holders `level` needs from a collection with `holders` snapshot holders. */
export function communityLevelRequirement(level: 2 | 3 | 4, holders: number): number {
  const milestone = COMMUNITY_MILESTONES.find((entry) => entry.level === level)!;
  // Integer basis points, so 3% of 2,000 is exactly 60 rather than a float a hair above it.
  const bps = Math.round(milestone.share * 10_000);
  return Math.max(milestone.min, Math.ceil((bps * holders) / 10_000));
}

/** The highest level `active` of `holders` reaches; 1 without a milestone. */
export function communityLevel(active: number, holders: number): CommunityLevel {
  let level: CommunityLevel = 1;
  for (const milestone of COMMUNITY_MILESTONES) {
    if (active >= communityLevelRequirement(milestone.level, holders)) level = milestone.level;
  }
  return level;
}

/** Bonus each active holder of a collection gets: its level's bonus plus its podium place's. */
export function communityBonus(level: CommunityLevel, podiumPlace: 1 | 2 | 3 | null): number {
  const milestone = COMMUNITY_MILESTONES.find((entry) => entry.level === level);
  return (milestone?.bonus ?? 0) + (podiumPlace === null ? 0 : COMMUNITY_PODIUM_BONUS[podiumPlace - 1]!);
}

/** The next level above `level` and the active holders it needs; `null` at level 4. */
export function communityNextLevel(level: CommunityLevel, active: number, holders: number): CommunityNextLevel | null {
  if (level === 4) return null;
  const next = (level + 1) as 2 | 3 | 4;
  const required = communityLevelRequirement(next, holders);
  return { level: next, required, remaining: Math.max(1, required - active) };
}

const countSchema = z.number().refine(Number.isInteger).meta({ type: "integer" }).nonnegative();
export const communityLevelSchema = z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]);
export const communityPodiumPlaceSchema = z.union([z.literal(1), z.literal(2), z.literal(3)]);
export const communityNextLevelSchema = z.object({
  level: z.union([z.literal(2), z.literal(3), z.literal(4)]),
  required: z.number().refine(Number.isInteger).meta({ type: "integer" }).positive(),
  remaining: z.number().refine(Number.isInteger).meta({ type: "integer" }).positive(),
}).strict();
export type CommunityNextLevel = z.infer<typeof communityNextLevelSchema>;

/** A collection's activation in the latest Aura snapshot, as stored there. */
export const communityActivationSchema = z.object({
  /** Distinct holder wallets in the badge's published snapshot (`H`). */
  holders: countSchema,
  /** Eligible members with the badge and an action badge (`A`). */
  active: countSchema,
  level: communityLevelSchema,
  nextLevel: communityNextLevelSchema.nullable(),
  podiumPlace: communityPodiumPlaceSchema.nullable(),
  /** Aura each active holder gets from this collection, before the member's community cap. */
  bonus: countSchema,
}).strict();
export type CommunityActivation = z.infer<typeof communityActivationSchema>;

const decimal = z.string().regex(/^(0|[1-9][0-9]*)$/);
const address = z.string().regex(/^0x[0-9a-fA-F]{40}$/).refine((value) => {
  try { return value === value.toLowerCase() || value === getAddress(value); } catch { return false; }
}, "Invalid address checksum");
export const holderSourceSchema = z.object({
  kind: z.enum(["erc721", "erc1155", "cryptopunks", "erc20_vault", "activation"]),
  address,
  deploymentBlock: decimal.optional(),
  wrapperAddresses: z.array(address).max(8).optional(),
  managerAddress: address.optional(),
  managerDeploymentBlock: decimal.optional(),
  tokenIds: z.array(decimal).max(100000).optional(),
}).strict().superRefine((source, context) => {
  if (source.kind === "activation" && source.managerAddress === undefined) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ["managerAddress"], message: "Activation sources require their manager" });
  }
  if (source.kind !== "activation" && (source.managerAddress !== undefined || source.managerDeploymentBlock !== undefined)) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ["managerAddress"], message: "Only activation sources have a manager" });
  }
  if (source.kind !== "cryptopunks" && source.wrapperAddresses !== undefined) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ["wrapperAddresses"], message: "Only canonical CryptoPunks sources have wrappers" });
  }
});
export const holderDraftSchema = z.object({
  slug: z.string().regex(/^[a-z0-9]+(?:_[a-z0-9]+)*$/).max(80),
  title: z.string().min(1).max(120), description: z.string().min(1).max(500),
  chainId: z.union([z.literal(1), z.literal(4663), z.literal(2741)]),
  snapshotAt: z.stringFormat("date-time", WIRE_DATETIME_PATTERN), sources: z.array(holderSourceSchema).min(1).max(16),
}).strict();
export const holderSnapshotSchema = z.object({
  id: z.guid(), blockNumber: decimal, blockHash: z.string(), blockTimestamp: z.stringFormat("date-time", WIRE_DATETIME_PATTERN),
  method: z.enum(["state", "logs"]), manifestSha256: z.string().regex(/^[0-9a-f]{64}$/),
  holderCount: z.number().refine(Number.isInteger).meta({ type: "integer" }).nonnegative(), providers: z.array(z.string()),
});
/** The admin Holdings tab's view of a collection in the latest Aura snapshot. */
export const holderAdminActivationSchema = z.object({
  holders: countSchema, active: countSchema, level: communityLevelSchema, podiumPlace: communityPodiumPlaceSchema.nullable(),
}).strict();
export const holderBadgeSchema = holderDraftSchema.extend({
  id: z.string(), status: z.enum(["draft", "published", "retired"]),
  snapshot: holderSnapshotSchema.nullable(),
  /** Admin list only: `null` elsewhere, and for collections the latest Aura snapshot did not score. */
  activation: holderAdminActivationSchema.nullable().default(null),
});
export const holderBadgesSchema = z.object({ badges: z.array(holderBadgeSchema) });
export const holderOverrideSchema = z.object({ personId: z.guid(), action: z.enum(["grant", "revoke"]), reason: z.string().trim().min(1).max(1000) }).strict();
/**
 * One published holder badge for the signed-in person. `claimed_elsewhere`: a linked wallet (or a
 * vault delegating to one) held a qualifying snapshot row, but an earlier-linked wallet of another
 * person already took that vault or token; `wallet`/`vault`/`tokenIds` then describe only this
 * person's own matching wallet, never the other claimant.
 */
export const holderEvidenceSchema = z.object({
  badgeId: z.string(), title: z.string(), snapshotAt: z.stringFormat("date-time", WIRE_DATETIME_PATTERN),
  status: z.enum(["found", "delegated", "claimed_elsewhere", "not_found", "granted", "revoked"]),
  snapshotId: z.guid().nullable(), wallet: address.nullable(), vault: address.nullable(),
  tokenIds: z.array(z.string()), stakedAmount: decimal.nullable(),
});
/**
 * Admin person detail: every published holder badge where the person has a claim, an override or
 * a candidate row. A person may claim several vaults for one badge. `candidates[].claimedBy` names
 * who holds that vault's claim for the badge (`null` when unclaimed), so a reviewer can see a
 * conflict without opening the census.
 */
export const holderAdminPersonBadgeSchema = z.object({
  badgeId: z.string(), title: z.string(), chainId: z.number().refine(Number.isInteger).meta({ type: "integer" }).positive(),
  claims: z.array(z.object({ vault: address, delegatedWallet: address.nullable(), tokenIds: z.array(z.string()), stakedAmount: decimal.nullable(), claimedAt: z.stringFormat("date-time", WIRE_DATETIME_PATTERN) }).strict()),
  override: z.object({ action: z.enum(["grant", "revoke"]), reason: z.string(), actor: z.string(), at: z.stringFormat("date-time", WIRE_DATETIME_PATTERN) }).strict().nullable(),
  candidates: z.array(z.object({
    vault: address, linkedWallet: address, tokenIds: z.array(z.string()), linkedAt: z.stringFormat("date-time", WIRE_DATETIME_PATTERN),
    claimedBy: z.guid().nullable(),
  }).strict()),
}).strict();
export const holderReviewSchema = z.object({
  rows: z.array(z.object({ wallet: address, tokenIds: z.array(z.string()), stakedAmount: decimal.nullable(),
    personId: z.guid().nullable(), delegatedWallet: address.nullable(), conflicts: z.array(z.string()) })),
  nextCursor: z.string().nullable(),
});
export type HolderSource = z.infer<typeof holderSourceSchema>;
export type HolderDraft = z.infer<typeof holderDraftSchema>;
export type HolderBadge = z.infer<typeof holderBadgeSchema>;
export type HolderSnapshot = z.infer<typeof holderSnapshotSchema>;
export type HolderEvidence = z.infer<typeof holderEvidenceSchema>;
export type HolderReview = z.infer<typeof holderReviewSchema>;
export const holderOverrideResponseSchema = z.object({ ok: z.literal(true) }).strict();
export type HolderOverride = z.infer<typeof holderOverrideSchema>;
export type HolderAdminPersonBadge = z.infer<typeof holderAdminPersonBadgeSchema>;
